import { Box, Image } from "@chakra-ui/react";

export type LogoTone = "mono" | "native";

type Props = {
  logo?: string;
  monogram?: string;
  alt: string;
  /**
   * How the artwork is normalized for the dark page.
   *
   * "mono" repaints the mark white, which only works for a transparent
   * single-color wordmark — a filled shape (the NASA meatball) or an opaque
   * field flattens into a white block.
   *
   * "native" leaves the original colors alone, for full-color marks. These
   * need either their own transparency or a black field, since the page
   * background is black.
   */
  tone?: LogoTone;
  /** Rendered height of the mark; width is derived from the artwork. */
  height?: string;
  maxWidth?: string;
};

// Brand artwork arrives in mixed colors and aspect ratios (a wide SpaceX
// wordmark, a full-color NASA meatball, a mission patch). Single-color
// wordmarks are normalized to white at a fixed height so they read as one
// system; full-color marks keep their own palette at that same height.
export default function LogoMark({
  logo,
  monogram,
  alt,
  tone = "mono",
  height = "28px",
  maxWidth = "132px",
}: Props) {
  if (logo) {
    const isMono = tone === "mono";
    return (
      <Image
        src={logo}
        alt={alt}
        h={height}
        maxW={maxWidth}
        w="auto"
        objectFit="contain"
        objectPosition="left center"
        filter={isMono ? "brightness(0) invert(1)" : undefined}
        opacity={isMono ? 0.85 : 0.95}
        transition="opacity 0.2s ease"
        _groupHover={{ opacity: 1 }}
        loading="lazy"
      />
    );
  }

  return (
    <Box
      h={height}
      px={2.5}
      display="inline-flex"
      alignItems="center"
      border="1px solid"
      borderColor="whiteAlpha.400"
      borderRadius="sm"
      fontFamily="mono"
      fontSize="xs"
      letterSpacing="0.12em"
      color="whiteAlpha.800"
      whiteSpace="nowrap"
    >
      {monogram ?? alt.slice(0, 4).toUpperCase()}
    </Box>
  );
}
